import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import CustomizedTabs from "../../../shared/components/navigations/tabs";
import ScopeCertificateComponent from "./sc-tabs/scope-certificate-details";
import FacilityDetailsComponent from "./sc-tabs/facility-details";
import ProductManagementComponent from "./sc-tabs/facility-product-details";
import CertifiedOrganizationDetails from "./sc-tabs/certified-organization-details/certified-organization-details";
import { useParams } from "react-router-dom";
// If you have a custom hook similar to `useApi`:
import { useApi } from "../../../shared/hooks/useApi";
import { API_ROUTES } from "../../../shared/app-configs/sc-app-config";

const ScForm = () => {
  // If you want to enable or disable the "disable logic"
  
  // Retrieve URL parameters.
  const { formtype, SC_ID } = useParams<{ formtype: string; SC_ID: string }>();
  const baseUrl = formtype ?? "create-sc";
  const enableDisableLogic : Boolean= formtype === 'submited-sc' ? false:true;

  // We'll store the API response in an object: { [tabId]: boolean }
  // For example: { "scope-certificate-details": true, "certified-organization-details": false, ... }
  const [completionMap, setCompletionMap] = useState<Record<string, boolean>>({});

  // If you have a custom API hook, initialize it here.
  // Otherwise, you can use fetch/axios directly.
  const { loading, executeGet } = useApi();

  /**
   * Fetch the completion data from your API.
   * For instance, if your endpoint is "v1/sc/get-disable-tabs" and returns an object like:
   * {
   *   "scope-certificate-details": true,
   *   "certified-organization-details": true,
   *   "facility-details": false,
   *   "facility-product-details": false
   * }
   * where `true` means "that tab is completed."
   */
  const fetchCompletionData = async () => {
    if (!SC_ID) return; // If there's no SC_ID, skip the call
    try {
      // Replace "YOUR_API_ENDPOINT_HERE" with your actual endpoint:
      const response: any = await executeGet(API_ROUTES.GET_DISABLE_TABS, {
        scId: SC_ID,
      });
      console.log("API completionMap:", response);
      setCompletionMap(response.data || {});
    } catch (error) {
      console.error("Error fetching tab completion status:", error);
    }
  };

  // Call the API when the component mounts or SC_ID changes.
  useEffect(() => {
    fetchCompletionData();
  }, [SC_ID]);

  /**
   * Called when user clicks "Next" in any tab, so we can re-fetch
   * the latest completion data.
   */
  const handleNextTab = async () => {
    await fetchCompletionData();
  };

  /**
   * Helper to see if a given tab is completed.
   */
  const isTabCompleted = (tabId: string) => {
    return !!completionMap[tabId];
  };

  // Define all tabs as mandatory in a specific order.
  const mandatoryTabs = [
    "scope-certificate-details",
    "certified-organization-details",
    "facility-details",
    "facility-product-details",
  ];

  // Build the base tabs array.
  const baseTabs = [
    {
      id: "scope-certificate-details",
      label: "Scope Certificate Details",
      component: <ScopeCertificateComponent onNextTab={handleNextTab} />,
    },
    {
      id: "certified-organization-details",
      label: "Certified Organization Details",
      component: <CertifiedOrganizationDetails onNextTab={handleNextTab} />,
    },
    {
      id: "facility-details",
      label: "Facility Details",
      component: <FacilityDetailsComponent onNextTab={handleNextTab} />,
    },
    {
      id: "facility-product-details",
      label: "Facility Product Details",
      component: <ProductManagementComponent completionMap={completionMap} onNextTab={handleNextTab}/>,
    },
  ];

  // Check if SC_ID is present.
  const hasSC_ID = Boolean(SC_ID);

  // Compute the final tabs array with or without disable logic.
  const tabs = baseTabs.map((tab) => {
    // If disable logic is turned off, enable all tabs.
    if (!enableDisableLogic) {
      return { ...tab, disabled: false };
    }

    // Otherwise, apply mandatory logic.
    let disabled = false;

    // The first tab is always enabled.
    if (tab.id === "scope-certificate-details") {
      disabled = false;
    } else {
      // For subsequent tabs, we need SC_ID and the previous mandatory tab to be completed.
      if (!hasSC_ID) {
        disabled = true;
      } else {
        // Find the index of the current tab in the mandatoryTabs array.
        const currentIndex = mandatoryTabs.indexOf(tab.id);
        if (currentIndex > 0) {
          const prevTabId = mandatoryTabs[currentIndex - 1];
          // If the previous tab is not completed, disable this one.
          if (!isTabCompleted(prevTabId)) {
            disabled = true;
          }
        }
      }
    }

    return { ...tab, disabled };
  });

  return (
    <>
      <Box sx={{ textAlign: "left" }} />
      <Box sx={{ mt: "2px" }}>
        <CustomizedTabs tabs={tabs} baseUrl={baseUrl} system="sc" ID={SC_ID} />
      </Box>
    </>
  );
};

export default ScForm;
